/** SUNTRONIX ONE — Customer 360 · Technology Environment.
 *
 *  Structured playbook editor for how a customer operates technology.
 *  Read-only for customer users unless they are customer_global_admin.
 *  Sensitive IDs (ABM Org, Tenant ID, MDM Server) are redacted server-side
 *  for non-Suntronix roles — the field simply won't appear.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Pin, AlertTriangle, Lock, Eye, Users, Building2, ShieldCheck } from "lucide-react";
import DevicesTab from "@/components/app/DevicesTab";

const TABS = [
  { key: "summary",    label: "Summary" },
  { key: "devices",    label: "Devices" },
  { key: "apple",      label: "Apple / ABM" },
  { key: "windows",    label: "Windows / Autopilot" },
  { key: "android",    label: "Android" },
  { key: "identity",   label: "Identity & SSO" },
  { key: "security",   label: "Security" },
  { key: "applecare",  label: "AppleCare+" },
  { key: "deployment", label: "Deployment" },
  { key: "warehouse",  label: "Warehouse" },
  { key: "reuse",      label: "Reuse" },
  { key: "recovery",   label: "Recovery" },
  { key: "erasure",    label: "Erasure" },
  { key: "repair",     label: "Repair" },
  { key: "itad",       label: "ITAD" },
  { key: "logistics",  label: "Logistics" },
  { key: "commercial", label: "Commercial" },
  { key: "notes",      label: "Notes" },
];

const YES_NO_UNKNOWN = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unknown", label: "Unknown" },
  { value: "not_applicable", label: "N/A" },
];

const REUSE_POLICY = [
  { value: "allowed", label: "Reuse Allowed" },
  { value: "not_allowed", label: "Reuse Not Allowed" },
  { value: "approval_required", label: "Approval Required" },
  { value: "role_dependent", label: "Role Dependent" },
  { value: "asset_dependent", label: "Asset Dependent" },
  { value: "country_dependent", label: "Country Dependent" },
];

export default function TechnologyEnvironment() {
  const { mcId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [env, setEnv] = useState(null);
  const [notes, setNotes] = useState([]);
  const [mdmOptions, setMdmOptions] = useState([]);
  const [idpOptions, setIdpOptions] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [masterClient, setMasterClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(searchParams.get("tab") || "summary");
  const [draft, setDraft] = useState({});

  const canEdit = user && (
    !user.master_company_id ||           // suntronix internal
    (user.role === "customer_global_admin" && user.master_company_id === mcId)
  );

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    Promise.all([
      api.get(`/customer-playbook/${mcId}/tech-env`),
      api.get(`/customer-playbook/${mcId}/notes`),
      api.get("/master-data/mdm_uem"),
      api.get("/master-data/identity_platform"),
      api.get("/master-companies").catch(() => ({ data: { items: [] } })),
    ]).then(([e, n, mdm, idp, mcs]) => {
      if (cancel) return;
      setEnv(e.data);
      setNotes(n.data.items || []);
      setMdmOptions(mdm.data.items || []);
      setIdpOptions(idp.data.items || []);
      const c = (mcs.data.items || []).find((m) => m.id === mcId);
      setCustomer(c || null);
      if (c?.master_client_id) {
        api.get(`/master-clients/${c.master_client_id}`).then(({ data }) => !cancel && setMasterClient(data)).catch(() => {});
      }
    }).catch(() => toast.error("Failed to load technology environment"))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [mcId]);

  useEffect(() => {
    setSearchParams(tab === "summary" ? {} : { tab }, { replace: true });
  }, [tab, setSearchParams]);

  const currentSection = useMemo(() => env?.[tab] || {}, [env, tab]);

  useEffect(() => {
    setDraft(currentSection);
  }, [currentSection]);

  const saveSection = async () => {
    setSaving(true);
    try {
      await api.put(`/customer-playbook/${mcId}/tech-env/section`, {
        section: tab, values: draft,
      });
      const { data } = await api.get(`/customer-playbook/${mcId}/tech-env`);
      setEnv(data);
      toast.success(`${TABS.find((t) => t.key === tab)?.label || tab} saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="p-6 text-sm text-[color:var(--st-text-muted)]">Loading playbook…</div>;
  }

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="tech-env">
      <PageHeader
        eyebrow="Customer 360 · Technology Environment"
        title={customer?.name || "Customer"}
        context={
          <span className="flex flex-wrap items-center gap-2">
            <span>Master client: <b>{masterClient?.name || "—"}</b></span>
            <span>·</span>
            <span>Playbook v{env?.version || 0}</span>
            <span>·</span>
            <span>Completeness: <b>{env?.completeness_pct ?? "—"}%</b></span>
          </span>
        }
        testId="tech-env-header"
      />

      <ProfileSummary env={env} />

      <div className="flex overflow-x-auto gap-1 border-b border-[color:var(--st-border)] -mx-2 px-2" data-testid="tech-env-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`tech-env-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={
              "px-3 py-2 text-[13px] whitespace-nowrap border-b-2 transition-colors " +
              (tab === t.key
                ? "border-[color:var(--st-accent)] text-[color:var(--st-text)] font-medium"
                : "border-transparent text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "summary" && <SummaryPanel env={env} customer={customer} masterClient={masterClient} notes={notes} />}
      {tab === "devices" && (
        <DevicesTab masterCompanyId={mcId} scopeLabel={customer?.name || "this company"} />
      )}
      {tab === "notes"   && <NotesPanel notes={notes} mcId={mcId} canEdit={canEdit} onReload={async () => {
        const { data } = await api.get(`/customer-playbook/${mcId}/notes`);
        setNotes(data.items || []);
      }} />}

      {tab !== "summary" && tab !== "notes" && tab !== "devices" && (
        <div className="st-panel p-5 space-y-4" data-testid={`tech-env-panel-${tab}`}>
          <SectionEditor
            section={tab}
            draft={draft}
            setDraft={setDraft}
            mdmOptions={mdmOptions}
            idpOptions={idpOptions}
            canEdit={canEdit}
          />
          {canEdit && (
            <div className="pt-3 border-t border-[color:var(--st-border)] flex items-center gap-2">
              <Button onClick={saveSection} disabled={saving} data-testid="tech-env-save">
                {saving ? "Saving…" : "Save section"}
              </Button>
              <span className="text-xs text-[color:var(--st-text-subtle)]">
                Changes are versioned. History is preserved.
              </span>
            </div>
          )}
          {!canEdit && (
            <div className="text-[12px] text-[color:var(--st-text-subtle)] flex items-center gap-1">
              <Lock className="h-3 w-3" strokeWidth={1.6} /> Read-only — contact your customer admin to change.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------- Profile summary tile ------------------------- */
function ProfileSummary({ env }) {
  if (!env) return null;
  const apple = env.apple || {};
  const windows = env.windows || {};
  const applecare = env.applecare || {};
  const warehouse = env.warehouse || {};
  const reuse = env.reuse || {};
  const erasure = env.erasure || {};
  const recovery = env.recovery || {};
  const items = [
    { label: "Apple Business Manager", value: labelize(apple.abm_enabled) },
    { label: "Primary MDM (Apple)", value: labelize(apple.primary_mdm) },
    { label: "Windows Autopilot", value: labelize(windows.autopilot_enabled) },
    { label: "AppleCare+", value: labelize(apple.applecare_plus || applecare.policy) },
    { label: "Reuse", value: labelize(reuse.policy) },
    { label: "Warehouse", value: labelize(warehouse.suntronix_warehousing) === "Yes" ? "Suntronix Managed" : labelize(warehouse.suntronix_warehousing) },
    { label: "Data Erasure", value: labelize(erasure.required) },
    { label: "Recovery", value: labelize(recovery.enabled) },
  ];
  return (
    <Section title="Profile summary" hint="How this customer operates technology." testId="tech-env-summary-tile">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((i) => (
          <div key={i.label} className="st-panel p-3 rounded-md" data-testid={`tech-env-summary-${slug(i.label)}`}>
            <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">{i.label}</div>
            <div className="text-sm font-medium text-[color:var(--st-text)] mt-1">{i.value || "—"}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function SummaryPanel({ env, customer, masterClient, notes }) {
  const pinned = notes.filter((n) => n.pinned);
  const critical = notes.filter((n) => n.critical);
  return (
    <div className="space-y-4">
      {critical.length > 0 && (
        <div className="st-panel p-4 border border-[color:var(--st-critical-soft)] bg-[color:var(--st-critical-soft)] rounded-md" data-testid="tech-env-critical-panel">
          <div className="flex items-center gap-2 text-sm font-semibold text-[color:var(--st-critical)] uppercase tracking-wider">
            <AlertTriangle className="h-4 w-4" strokeWidth={1.8} /> Critical operating instructions
          </div>
          <ul className="mt-2 space-y-2">
            {critical.map((n) => (
              <li key={n.id} className="text-[13px]">
                <div className="font-medium">{n.title}</div>
                <div className="text-[color:var(--st-text-muted)]">{n.body}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {pinned.length > 0 && (
        <Section title="Pinned notes" hint="Surface these during ops workflows." testId="tech-env-pinned">
          <div className="grid gap-2">
            {pinned.map((n) => (
              <div key={n.id} className="st-panel p-3 flex items-start gap-3" data-testid={`tech-env-pinned-note-${n.id}`}>
                <Pin className="h-4 w-4 mt-0.5 text-[color:var(--st-accent)]" strokeWidth={1.8} />
                <div className="flex-1">
                  <div className="text-sm font-medium">{n.title}</div>
                  <div className="text-[13px] text-[color:var(--st-text-muted)]">{n.body}</div>
                  <div className="mt-1"><VisibilityBadge visibility={n.visibility} /></div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
      {env?.missing_fields?.length > 0 && (
        <Section title="Profile gaps" hint="Complete these to raise operational confidence." testId="tech-env-gaps">
          <div className="flex flex-wrap gap-2">
            {env.missing_fields.map((f) => (
              <span key={f} className="px-2 py-1 rounded-md bg-[color:var(--st-warn-soft)] text-[color:var(--st-warn)] text-[12px] font-mono">
                {f}
              </span>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

/* --------------------------- Section editor ------------------------------ */
function SectionEditor({ section, draft, setDraft, mdmOptions, idpOptions, canEdit }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const props = { draft, set, canEdit, mdmOptions, idpOptions };
  switch (section) {
    case "apple":      return <AppleFields {...props} />;
    case "windows":    return <WindowsFields {...props} />;
    case "android":    return <AndroidFields {...props} />;
    case "identity":   return <IdentityFields {...props} />;
    case "security":   return <SecurityFields {...props} />;
    case "applecare":  return <AppleCareFields {...props} />;
    case "deployment": return <DeploymentFields {...props} />;
    case "warehouse":  return <WarehouseFields {...props} />;
    case "reuse":      return <ReuseFields {...props} />;
    case "recovery":   return <RecoveryFields {...props} />;
    case "erasure":    return <ErasureFields {...props} />;
    case "repair":     return <RepairFields {...props} />;
    case "itad":       return <ITADFields {...props} />;
    case "logistics":  return <LogisticsFields {...props} />;
    case "commercial": return <CommercialFields {...props} />;
    default:           return <div className="text-sm text-[color:var(--st-text-muted)]">Coming soon.</div>;
  }
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}
function Field({ label, children, sensitive }) {
  return (
    <div>
      <Label className="text-[12px] flex items-center gap-1">
        {label}
        {sensitive && <span className="text-[10px] text-[color:var(--st-text-subtle)] flex items-center gap-0.5"><Lock className="h-2.5 w-2.5" /> internal</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function TriState({ value, onChange, disabled }) {
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
      <SelectContent>
        {YES_NO_UNKNOWN.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
function TextField({ value, onChange, disabled, placeholder }) {
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder={placeholder} />;
}

function AppleFields({ draft, set, canEdit, mdmOptions }) {
  return (
    <div className="space-y-6">
      <Row>
        <Field label="Apple environment"><TriState value={draft.environment} onChange={(v) => set("environment", v)} disabled={!canEdit} /></Field>
        <Field label="Apple Business Manager"><TriState value={draft.abm_enabled} onChange={(v) => set("abm_enabled", v)} disabled={!canEdit} /></Field>
        <Field label="ABM Organisation ID" sensitive><TextField value={draft.abm_org_id} onChange={(v) => set("abm_org_id", v)} disabled={!canEdit} /></Field>
        <Field label="ABM Organisation Name"><TextField value={draft.abm_org_name} onChange={(v) => set("abm_org_name", v)} disabled={!canEdit} /></Field>
        <Field label="Automated Device Enrollment"><TriState value={draft.ade_enabled} onChange={(v) => set("ade_enabled", v)} disabled={!canEdit} /></Field>
        <Field label="Federated Authentication"><TriState value={draft.federated_auth} onChange={(v) => set("federated_auth", v)} disabled={!canEdit} /></Field>
        <Field label="Primary Apple MDM">
          <Select value={draft.primary_mdm || ""} onValueChange={(v) => set("primary_mdm", v)} disabled={!canEdit}>
            <SelectTrigger><SelectValue placeholder="Select MDM…" /></SelectTrigger>
            <SelectContent>{mdmOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="MDM Server Name" sensitive><TextField value={draft.mdm_server_name} onChange={(v) => set("mdm_server_name", v)} disabled={!canEdit} /></Field>
        <Field label="Apple Deployment Method"><TextField value={draft.deployment_method} onChange={(v) => set("deployment_method", v)} disabled={!canEdit} /></Field>
        <Field label="AppleCare+"><TriState value={draft.applecare_plus} onChange={(v) => set("applecare_plus", v)} disabled={!canEdit} /></Field>
      </Row>
      <Field label="Apple Deployment Notes"><Textarea rows={3} value={draft.applecare_notes || ""} onChange={(e) => set("applecare_notes", e.target.value)} disabled={!canEdit} /></Field>
    </div>
  );
}

function WindowsFields({ draft, set, canEdit, mdmOptions }) {
  return (
    <Row>
      <Field label="Windows Autopilot"><TriState value={draft.autopilot_enabled} onChange={(v) => set("autopilot_enabled", v)} disabled={!canEdit} /></Field>
      <Field label="Primary Management Platform">
        <Select value={draft.primary_mgmt_platform || ""} onValueChange={(v) => set("primary_mgmt_platform", v)} disabled={!canEdit}>
          <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{mdmOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Tenant Reference" sensitive><TextField value={draft.tenant_ref} onChange={(v) => set("tenant_ref", v)} disabled={!canEdit} /></Field>
      <Field label="Group Tag" sensitive><TextField value={draft.group_tag} onChange={(v) => set("group_tag", v)} disabled={!canEdit} /></Field>
      <Field label="Microsoft Entra ID"><TriState value={draft.entra_id} onChange={(v) => set("entra_id", v)} disabled={!canEdit} /></Field>
      <Field label="Cloud Join"><TriState value={draft.cloud_join} onChange={(v) => set("cloud_join", v)} disabled={!canEdit} /></Field>
      <Field label="BitLocker Required"><TextField value={draft.bitlocker} onChange={(v) => set("bitlocker", v)} disabled={!canEdit} /></Field>
      <Field label="Windows Hello"><TextField value={draft.windows_hello} onChange={(v) => set("windows_hello", v)} disabled={!canEdit} /></Field>
      <Field label="Windows Standard Build"><TextField value={draft.standard_build} onChange={(v) => set("standard_build", v)} disabled={!canEdit} /></Field>
    </Row>
  );
}
function AndroidFields({ draft, set, canEdit, mdmOptions }) {
  return (
    <Row>
      <Field label="Android used"><TriState value={draft.used} onChange={(v) => set("used", v)} disabled={!canEdit} /></Field>
      <Field label="Android Enterprise"><TriState value={draft.enterprise} onChange={(v) => set("enterprise", v)} disabled={!canEdit} /></Field>
      <Field label="Zero-Touch Enrollment"><TriState value={draft.zero_touch} onChange={(v) => set("zero_touch", v)} disabled={!canEdit} /></Field>
      <Field label="Primary Android MDM/UEM">
        <Select value={draft.primary_mdm || ""} onValueChange={(v) => set("primary_mdm", v)} disabled={!canEdit}>
          <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{mdmOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
    </Row>
  );
}
function IdentityFields({ draft, set, canEdit, idpOptions }) {
  return (
    <Row>
      <Field label="Primary IdP">
        <Select value={draft.primary_idp || ""} onValueChange={(v) => set("primary_idp", v)} disabled={!canEdit}>
          <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{idpOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="SSO"><TriState value={draft.sso} onChange={(v) => set("sso", v)} disabled={!canEdit} /></Field>
      <Field label="SCIM"><TriState value={draft.scim} onChange={(v) => set("scim", v)} disabled={!canEdit} /></Field>
      <Field label="Federation"><TriState value={draft.federation} onChange={(v) => set("federation", v)} disabled={!canEdit} /></Field>
      <Field label="User Provisioning"><TextField value={draft.provisioning} onChange={(v) => set("provisioning", v)} disabled={!canEdit} /></Field>
    </Row>
  );
}
function SecurityFields({ draft, set, canEdit }) {
  const fields = [
    ["edr", "EDR / XDR"], ["disk_encryption", "Disk Encryption"], ["filevault", "FileVault"],
    ["bitlocker", "BitLocker"], ["remote_wipe", "Remote Wipe"], ["activation_lock", "Activation Lock Policy"],
    ["local_admin", "Local Admin Policy"], ["usb", "USB Policy"], ["dlp", "DLP"],
    ["vpn", "VPN"], ["baseline", "Security Baseline"], ["compliance", "Compliance Requirement"],
  ];
  return (
    <Row>
      {fields.map(([k, l]) => (
        <Field key={k} label={l}><TextField value={draft[k]} onChange={(v) => set(k, v)} disabled={!canEdit} /></Field>
      ))}
    </Row>
  );
}
function AppleCareFields({ draft, set, canEdit }) {
  return (
    <Row>
      <Field label="Policy">
        <Select value={draft.policy || ""} onValueChange={(v) => set("policy", v)} disabled={!canEdit}>
          <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes — mandatory</SelectItem>
            <SelectItem value="no">No</SelectItem>
            <SelectItem value="optional">Optional / By Request</SelectItem>
            <SelectItem value="product_dependent">Product Dependent</SelectItem>
            <SelectItem value="approval_required">Customer Approval Required</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Coverage Type"><TextField value={draft.coverage_type} onChange={(v) => set("coverage_type", v)} disabled={!canEdit} /></Field>
      <Field label="Duration"><TextField value={draft.duration} onChange={(v) => set("duration", v)} disabled={!canEdit} /></Field>
      <Field label="Verify before repair"><TriState value={draft.verify_before_repair ? "yes" : "no"} onChange={(v) => set("verify_before_repair", v === "yes")} disabled={!canEdit} /></Field>
    </Row>
  );
}
function DeploymentFields({ draft, set, canEdit }) {
  return (
    <Row>
      <Field label="Zero-Touch"><TriState value={draft.zero_touch} onChange={(v) => set("zero_touch", v)} disabled={!canEdit} /></Field>
      <Field label="MDM Enrollment Required"><TriState value={draft.mdm_enrollment_required ? "yes" : "no"} onChange={(v) => set("mdm_enrollment_required", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Asset Tag Required"><TriState value={draft.asset_tag_required ? "yes" : "no"} onChange={(v) => set("asset_tag_required", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Employee Ack Required"><TriState value={draft.ack_required ? "yes" : "no"} onChange={(v) => set("ack_required", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Packaging Standard"><TextField value={draft.packaging_standard} onChange={(v) => set("packaging_standard", v)} disabled={!canEdit} /></Field>
      <Field label="Shipment Method"><TextField value={draft.shipment_method} onChange={(v) => set("shipment_method", v)} disabled={!canEdit} /></Field>
      <Field label="Delivery Evidence"><TextField value={draft.delivery_evidence} onChange={(v) => set("delivery_evidence", v)} disabled={!canEdit} /></Field>
    </Row>
  );
}
function WarehouseFields({ draft, set, canEdit }) {
  return (
    <Row>
      <Field label="Suntronix Warehousing"><TriState value={draft.suntronix_warehousing} onChange={(v) => set("suntronix_warehousing", v)} disabled={!canEdit} /></Field>
      <Field label="Customer-owned stock held"><TriState value={draft.customer_stock_held} onChange={(v) => set("customer_stock_held", v)} disabled={!canEdit} /></Field>
      <Field label="Free storage days"><TextField value={draft.free_storage_days} onChange={(v) => set("free_storage_days", v)} disabled={!canEdit} /></Field>
      <Field label="Cycle count"><TextField value={draft.cycle_count} onChange={(v) => set("cycle_count", v)} disabled={!canEdit} /></Field>
    </Row>
  );
}
function ReuseFields({ draft, set, canEdit }) {
  return (
    <Row>
      <Field label="Reuse policy">
        <Select value={draft.policy || ""} onValueChange={(v) => set("policy", v)} disabled={!canEdit}>
          <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{REUSE_POLICY.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Minimum grade"><TextField value={draft.min_grade} onChange={(v) => set("min_grade", v)} disabled={!canEdit} placeholder="A / B / C" /></Field>
      <Field label="Warranty required"><TriState value={draft.warranty_required ? "yes" : "no"} onChange={(v) => set("warranty_required", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Same model required"><TriState value={draft.same_model_required ? "yes" : "no"} onChange={(v) => set("same_model_required", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Cross-company allowed"><TriState value={draft.cross_company ? "yes" : "no"} onChange={(v) => set("cross_company", v === "yes")} disabled={!canEdit} /></Field>
    </Row>
  );
}
function RecoveryFields({ draft, set, canEdit }) {
  return (
    <Row>
      <Field label="Employee Recovery"><TriState value={draft.enabled} onChange={(v) => set("enabled", v)} disabled={!canEdit} /></Field>
      <Field label="Home Pickup"><TriState value={draft.home_pickup ? "yes" : "no"} onChange={(v) => set("home_pickup", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Office Pickup"><TriState value={draft.office_pickup ? "yes" : "no"} onChange={(v) => set("office_pickup", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Pickup Attempts"><TextField value={draft.pickup_attempts} onChange={(v) => set("pickup_attempts", v)} disabled={!canEdit} /></Field>
      <Field label="SLA"><TextField value={draft.sla} onChange={(v) => set("sla", v)} disabled={!canEdit} /></Field>
      <Field label="Packaging by Suntronix"><TriState value={draft.packaging_by_suntronix ? "yes" : "no"} onChange={(v) => set("packaging_by_suntronix", v === "yes")} disabled={!canEdit} /></Field>
    </Row>
  );
}
function ErasureFields({ draft, set, canEdit }) {
  return (
    <Row>
      <Field label="Erasure Required"><TriState value={draft.required} onChange={(v) => set("required", v)} disabled={!canEdit} /></Field>
      <Field label="Standard / Method"><TextField value={draft.standard} onChange={(v) => set("standard", v)} disabled={!canEdit} placeholder="NIST 800-88" /></Field>
      <Field label="Certificate Required"><TriState value={draft.certificate_required ? "yes" : "no"} onChange={(v) => set("certificate_required", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Provider"><TextField value={draft.provider} onChange={(v) => set("provider", v)} disabled={!canEdit} /></Field>
    </Row>
  );
}
function RepairFields({ draft, set, canEdit }) {
  return (
    <Row>
      <Field label="Repair Allowed"><TriState value={draft.allowed} onChange={(v) => set("allowed", v)} disabled={!canEdit} /></Field>
      <Field label="Warranty First"><TriState value={draft.warranty_first ? "yes" : "no"} onChange={(v) => set("warranty_first", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Estimate Approval Required"><TriState value={draft.estimate_approval_required ? "yes" : "no"} onChange={(v) => set("estimate_approval_required", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Approval Threshold"><TextField value={draft.approval_threshold} onChange={(v) => set("approval_threshold", v)} disabled={!canEdit} /></Field>
      <Field label="Loaner during repair"><TriState value={draft.loaner_during_repair ? "yes" : "no"} onChange={(v) => set("loaner_during_repair", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="BER Rule"><TextField value={draft.ber_rule} onChange={(v) => set("ber_rule", v)} disabled={!canEdit} /></Field>
    </Row>
  );
}
function ITADFields({ draft, set, canEdit }) {
  return (
    <Row>
      <Field label="ITAD Enabled"><TriState value={draft.enabled} onChange={(v) => set("enabled", v)} disabled={!canEdit} /></Field>
      <Field label="Buyback Allowed"><TriState value={draft.buyback_allowed ? "yes" : "no"} onChange={(v) => set("buyback_allowed", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Recycle Enabled"><TriState value={draft.recycle_enabled ? "yes" : "no"} onChange={(v) => set("recycle_enabled", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Physical Destruction"><TextField value={draft.physical_destruction} onChange={(v) => set("physical_destruction", v)} disabled={!canEdit} /></Field>
      <Field label="Certificate Required"><TriState value={draft.certificate_required ? "yes" : "no"} onChange={(v) => set("certificate_required", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Preferred Partner"><TextField value={draft.preferred_partner} onChange={(v) => set("preferred_partner", v)} disabled={!canEdit} /></Field>
    </Row>
  );
}
function LogisticsFields({ draft, set, canEdit }) {
  return (
    <Row>
      <Field label="Preferred Courier"><TextField value={draft.preferred_courier} onChange={(v) => set("preferred_courier", v)} disabled={!canEdit} /></Field>
      <Field label="Backup Courier"><TextField value={draft.backup_courier} onChange={(v) => set("backup_courier", v)} disabled={!canEdit} /></Field>
      <Field label="POD Required"><TriState value={draft.pod_required ? "yes" : "no"} onChange={(v) => set("pod_required", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="OTP Delivery"><TriState value={draft.otp_delivery ? "yes" : "no"} onChange={(v) => set("otp_delivery", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="International Shipping"><TriState value={draft.international ? "yes" : "no"} onChange={(v) => set("international", v === "yes")} disabled={!canEdit} /></Field>
    </Row>
  );
}
function CommercialFields({ draft, set, canEdit }) {
  return (
    <Row>
      <Field label="Contract"><TextField value={draft.contract} onChange={(v) => set("contract", v)} disabled={!canEdit} /></Field>
      <Field label="Service Package"><TextField value={draft.service_package} onChange={(v) => set("service_package", v)} disabled={!canEdit} /></Field>
      <Field label="Billing Entity"><TextField value={draft.billing_entity} onChange={(v) => set("billing_entity", v)} disabled={!canEdit} /></Field>
      <Field label="Currency"><TextField value={draft.currency} onChange={(v) => set("currency", v)} disabled={!canEdit} placeholder="USD" /></Field>
      <Field label="PO Required"><TriState value={draft.po_required ? "yes" : "no"} onChange={(v) => set("po_required", v === "yes")} disabled={!canEdit} /></Field>
      <Field label="Invoice Frequency"><TextField value={draft.invoice_frequency} onChange={(v) => set("invoice_frequency", v)} disabled={!canEdit} /></Field>
    </Row>
  );
}

/* ------------------------------ Notes tab -------------------------------- */
function NotesPanel({ notes, mcId, canEdit, onReload }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", visibility: "CUSTOMER_USERS", pinned: false, critical: false });
  const submit = async () => {
    try {
      await api.post("/customer-playbook/notes", { master_company_id: mcId, ...form, contexts: ["any"] });
      toast.success("Note added");
      setForm({ title: "", body: "", visibility: "CUSTOMER_USERS", pinned: false, critical: false });
      setShowForm(false); onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="tech-env-notes-panel">
      {canEdit && !showForm && (
        <Button variant="create" onClick={() => setShowForm(true)} data-testid="tech-env-note-add">Add note</Button>
      )}
      {canEdit && showForm && (
        <div className="st-panel p-4 space-y-3" data-testid="tech-env-note-form">
          <Row>
            <Field label="Title"><TextField value={form.title} onChange={(v) => setForm({ ...form, title: v })} /></Field>
            <Field label="Visibility">
              <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INTERNAL">Internal (Suntronix only)</SelectItem>
                  <SelectItem value="MASTER_CLIENT">Master Client Only</SelectItem>
                  <SelectItem value="CUSTOMER_ADMIN">Customer Admin</SelectItem>
                  <SelectItem value="CUSTOMER_USERS">Customer Users</SelectItem>
                  <SelectItem value="PARTNER">Partner</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </Row>
          <Field label="Body"><Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></Field>
          <div className="flex items-center gap-3">
            <label className="text-[13px] flex items-center gap-1.5"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} /> Pinned</label>
            <label className="text-[13px] flex items-center gap-1.5"><input type="checkbox" checked={form.critical} onChange={(e) => setForm({ ...form, critical: e.target.checked })} /> Critical</label>
            <Button onClick={submit} data-testid="tech-env-note-save">Save</Button>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}
      {notes.length === 0 && <div className="text-sm text-[color:var(--st-text-muted)]">No notes yet.</div>}
      <div className="space-y-2">
        {notes.map((n) => (
          <div key={n.id} className="st-panel p-3" data-testid={`tech-env-note-${n.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {n.critical && <Badge variant="destructive">CRITICAL</Badge>}
                  {n.pinned && <Pin className="h-3.5 w-3.5 text-[color:var(--st-accent)]" />}
                  <div className="text-sm font-medium">{n.title}</div>
                  <VisibilityBadge visibility={n.visibility} />
                </div>
                <div className="text-[13px] text-[color:var(--st-text-muted)] mt-1">{n.body}</div>
                {n.contexts?.length > 0 && (
                  <div className="mt-1 flex gap-1 flex-wrap">
                    {n.contexts.map((c) => <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-[color:var(--st-surface-alt)] text-[color:var(--st-text-subtle)]">{c}</span>)}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VisibilityBadge({ visibility }) {
  const map = {
    INTERNAL:       { icon: Lock,       label: "Internal",         cls: "bg-red-50 text-red-700 border-red-200" },
    MASTER_CLIENT:  { icon: Building2,  label: "Master Client",    cls: "bg-purple-50 text-purple-700 border-purple-200" },
    CUSTOMER_ADMIN: { icon: ShieldCheck,label: "Customer Admin",   cls: "bg-blue-50 text-blue-700 border-blue-200" },
    CUSTOMER_USERS: { icon: Users,      label: "Customer Users",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    PARTNER:        { icon: Eye,        label: "Partner",          cls: "bg-amber-50 text-amber-700 border-amber-200" },
  };
  const v = map[visibility] || map.INTERNAL;
  const Icon = v.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${v.cls}`}>
      <Icon className="h-2.5 w-2.5" strokeWidth={2} /> {v.label}
    </span>
  );
}

/* helpers */
function labelize(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (!v) return "—";
  const map = {
    yes: "Yes", no: "No", unknown: "Unknown", not_applicable: "N/A",
    allowed: "Allowed", not_allowed: "Not Allowed", approval_required: "Approval Required",
    role_dependent: "Role Dependent", asset_dependent: "Asset Dependent", country_dependent: "Country Dependent",
    jamf_pro: "Jamf Pro", intune: "Microsoft Intune", kandji: "Kandji", workspace_one: "Workspace ONE",
  };
  return map[v] || (typeof v === "string" ? v : String(v));
}
function slug(s) { return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-"); }
